import { BACKEND_ERROR_CODE, createRequest } from '@sa/axios';
import { $t } from '@/locales';
import { useAuthStore } from '@/store/modules/auth';
import { getServiceBaseURL } from '@/utils/service';
import { getAuthorization, showErrorMsg } from './shared';
import type { RequestInstanceState } from './type';

const isHttpProxy = import.meta.env.DEV && import.meta.env.VITE_HTTP_PROXY === 'Y';
/**
 * 如果开启代理返回的baseUrl和otherBaseUrl就是代理前缀/proxy-xxx，默认的为proxy-default
 * 如果没有开启代理返回的baseUrl和otherBaseURL就是正常的baseUrl和otherBaseURL
 */
const { baseURL } = getServiceBaseURL(import.meta.env, isHttpProxy);
const MING_URL_PREFIX = '/api/mingshi/v1';

// export const request = createFlatRequest<App.Service.Response, RequestInstanceState>(
//   {
//     baseURL,
//     headers: {
//       apifoxToken: 'XL299LiMEDZ0H5h3A29PxwQXdMJqWyY2'
//     }
//   },
//   {
//     async onRequest(config) {
//       const Authorization = getAuthorization();
//       Object.assign(config.headers, { Authorization });

//       return config;
//     },
//     isBackendSuccess(response) {
//       // when the backend response code is "0000"(default), it means the request is success
//       // to change this logic by yourself, you can modify the `VITE_SERVICE_SUCCESS_CODE` in `.env` file
//       return String(response.data.code) === import.meta.env.VITE_SERVICE_SUCCESS_CODE;
//     },
//     async onBackendFail(response, instance) {
//       const authStore = useAuthStore();
//       const responseCode = String(response.data.code);

//       function handleLogout() {
//         authStore.resetStore();
//       }

//       function logoutAndCleanup() {
//         handleLogout();
//         window.removeEventListener('beforeunload', handleLogout);

//         request.state.errMsgStack = request.state.errMsgStack.filter(msg => msg !== response.data.msg);
//       }

//       // when the backend response code is in `logoutCodes`, it means the user will be logged out and redirected to login page
//       const logoutCodes = import.meta.env.VITE_SERVICE_LOGOUT_CODES?.split(',') || [];
//       if (logoutCodes.includes(responseCode)) {
//         handleLogout();
//         return null;
//       }

//       // when the backend response code is in `modalLogoutCodes`, it means the user will be logged out by displaying a modal
//       const modalLogoutCodes = import.meta.env.VITE_SERVICE_MODAL_LOGOUT_CODES?.split(',') || [];
//       if (modalLogoutCodes.includes(responseCode) && !request.state.errMsgStack?.includes(responseCode)) {
//         request.state.errMsgStack = [...(request.state.errMsgStack || []), response.data.msg];

//         // prevent the user from refreshing the page
//         window.addEventListener('beforeunload', handleLogout);

//         window.$modal?.error({
//           title: $t('common.error'),
//           content: response.data.msg,
//           okText: $t('common.confirm'),
//           maskClosable: false,
//           onOk() {
//             logoutAndCleanup();
//           },
//           onCancel() {
//             logoutAndCleanup();
//           }
//         });

//         return null;
//       }

//       // when the backend response code is in `expiredTokenCodes`, it means the token is expired, and refresh token
//       // the api `refreshToken` can not return error code in `expiredTokenCodes`, otherwise it will be a dead loop, should return `logoutCodes` or `modalLogoutCodes`
//       const expiredTokenCodes = import.meta.env.VITE_SERVICE_EXPIRED_TOKEN_CODES?.split(',') || [];
//       if (expiredTokenCodes.includes(responseCode)) {
//         const success = await handleExpiredRequest(request.state);
//         if (success) {
//           const Authorization = getAuthorization();
//           Object.assign(response.config.headers, { Authorization });

//           return instance.request(response.config) as Promise<AxiosResponse>;
//         }
//       }

//       return null;
//     },
//     transformBackendResponse(response) {
//       return response.data.data;
//     },
//     onError(error) {
//       // when the request is fail, you can show error message

//       let message = error.message;
//       let backendErrorCode = '';

//       // get backend error message and code
//       if (error.code === BACKEND_ERROR_CODE) {
//         message = error.response?.data?.msg || message;
//         backendErrorCode = String(error.response?.data?.code) || '';
//       }

//       // the error message is displayed in the modal
//       const modalLogoutCodes = import.meta.env.VITE_SERVICE_MODAL_LOGOUT_CODES?.split(',') || [];
//       if (modalLogoutCodes.includes(backendErrorCode)) {
//         return;
//       }

//       // when the token is expired, refresh token and retry request, so no need to show error message
//       const expiredTokenCodes = import.meta.env.VITE_SERVICE_EXPIRED_TOKEN_CODES?.split(',') || [];
//       if (expiredTokenCodes.includes(backendErrorCode)) {
//         return;
//       }

// showErrorMsg(request.state, message);
//     }
//   }
// );

/** createRequest 创建请求实例，服务端返回什么，这里就返回什么 */
export const request = createRequest<App.Service.Response, RequestInstanceState>(
  {
    baseURL: `${baseURL}${MING_URL_PREFIX}`,
    withCredentials: true,
    timeout: 1000 * 30,
    headers: {
      application: 'application/json',
      'LICENSE-KEY': import.meta.env.VITE_LICENSE_KEY || ''
    }
  },
  {
    /**
     * 请求拦截器，可以在这里设置请求头
     *
     * @param config 请求配置
     * @returns 请求配置
     */
    async onRequest(config) {
      const { headers } = config;

      // 设置token
      const token = getAuthorization();
      const WEBAPP_KEY = token || '';
      Object.assign(headers, { WEBAPP_KEY });

      return config;
    },
    isBackendSuccess(response) {
      // 当请求返回的code为0时，认为请求成功
      return String(response?.data?.code) === import.meta.env.VITE_SERVICE_SUCCESS_CODE;
    },
    async onBackendFail(response) {
      const authStore = useAuthStore();
      const responseCode = String(response?.data?.code);

      // 登出
      const handleLogout = () => {
        authStore.resetStore();
      };

      // 登出并清空错误信息栈
      const logoutAndCleanup = () => {
        handleLogout();
        window.removeEventListener('beforeunload', handleLogout);
        request.state.errMsgStack = request.state.errMsgStack.filter(msg => msg !== response?.data?.msg);
      };

      // 当后端返回这些返回码的时候用户登出
      const logoutCodes = import.meta.env.VITE_SERVICE_LOGOUT_CODES?.split(',') || [];
      if (logoutCodes.includes(responseCode)) {
        handleLogout();
        return null;
      }

      // 当后端返回这些返回码的时候弹出提示框用户登出
      const modalLogoutCodes = import.meta.env.VITE_SERVICE_MODAL_LOGOUT_CODES?.split(',') || [];
      if (modalLogoutCodes.includes(responseCode) && !request.state.errMsgStack?.includes(responseCode)) {
        request.state.errMsgStack = [...(request.state.errMsgStack || []), response?.data?.msg];
        window.addEventListener('beforeunload', handleLogout);

        window.$modal?.error({
          title: $t('common.error'),
          content: response.data.msg,
          okText: $t('common.confirm'),
          maskClosable: false,
          onOk() {
            logoutAndCleanup();
          },
          onCancel() {
            logoutAndCleanup();
          }
        });

        return null;
      }

      // 当后端返回这些错误码时，刷新token
      const expiredTokenCodes = import.meta.env.VITE_SERVICE_EXPIRED_TOKEN_CODES?.split(',') || [];
      if (expiredTokenCodes.includes(responseCode)) {
        // 刷新token
        // const success = await handleExpiredRequest(request?.state);
        // if (success) {
        //   const Authorization = getAuthorization();
        //   Object.assign(response.config.headers, { Authorization });
        //   return instance.request(response.config) as Promise<AxiosResponse>;
        // }
      }

      return null;
    },
    transformBackendResponse(response) {
      return response?.data;
    },
    onError(error) {
      let message = error?.message;

      if (error?.response?.status === 403 || String(error?.response?.data?.code) === '403') {
        showErrorMsg(request.state, '当前角色无权限访问该功能');
        return;
      }

      // show backend error message
      if (error?.code === BACKEND_ERROR_CODE) {
        message = error?.response?.data?.msg || message;
      }

      showErrorMsg(request.state, message);
    }
  }
);
